/**
 * @file Overlay.jsx
 * @location src/components/modals/Overlay.jsx
 * @role 모달 배경 오버레이 래퍼 — document.body 로 portal.
 *
 * createPortal 사용 이유:
 *   - 페이지 DOM 트리 내 CSS overflow / transform / contain 의 영향을 차단
 *   - z-index 충돌 가능성 최소화
 *   - 인쇄 모드 등에서 page chrome 과 분리
 *
 * 동작:
 *   - 배경(.moverlay) 클릭 시 onClose 호출 (e.target === e.currentTarget 체크로 모달 내부 클릭은 무시)
 *   - ESC 키로 닫기 + Tab/Shift+Tab 포커스 트랩 + 첫 입력 자동 포커스
 *   - 해제 시 이전 포커스 복귀
 *   - AlertModal · ConfirmModal · 그 외 모든 모달이 공통 사용
 */
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// 배경 스크롤 락 — 모달 중첩 시 가장 바깥 모달이 닫힐 때만 복원하도록 카운트
let scrollLockCount = 0;
let scrollLockPrev = '';

export function Overlay({ children, onClose, closing }) {
  const ref = useRef(null);
  const titleId = useId();

  // 배경(body) 스크롤 락 — iOS 등에서 모달 뒤 콘텐츠 터치 스크롤 방지
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (scrollLockCount === 0) {
      scrollLockPrev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    scrollLockCount += 1;
    return () => {
      scrollLockCount -= 1;
      if (scrollLockCount === 0) document.body.style.overflow = scrollLockPrev;
    };
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const prevActive = document.activeElement;

    // 모달 제목(.mtit) 자동 id 부여 + aria-labelledby 연결 — 스크린리더 announce
    const mtit = node.querySelector('.mtit');
    if (mtit) {
      if (!mtit.id) mtit.id = titleId;
      node.setAttribute('aria-labelledby', mtit.id);
    }

    // 첫 포커스 가능한 요소로 이동 (textarea > input > button 순 우선)
    const focusables = () => Array.from(node.querySelectorAll(FOCUSABLE)).filter(el => el.offsetParent !== null);
    const list = focusables();
    const firstInput = list.find(el => el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && el.type !== 'button'))
                    ?? list.find(el => !el.classList.contains('mclose'))
                    ?? list[0];
    firstInput?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last  = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { last.focus();  e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
    };
    node.addEventListener('keydown', onKey);
    return () => {
      node.removeEventListener('keydown', onKey);
      if (prevActive && typeof prevActive.focus === 'function') prevActive.focus();
    };
  }, [onClose, titleId]);

  // SSR-safe portal — document 없으면 inline (테스트/SSR 환경)
  if (typeof document === 'undefined') {
    return (
      <div ref={ref} className={`moverlay${closing ? ' closing' : ''}`} role="dialog" aria-modal="true"
        onClick={e => e.target === e.currentTarget && onClose()}>
        {children}
      </div>
    );
  }

  return createPortal(
    <div
      ref={ref}
      className={`moverlay${closing ? ' closing' : ''}`}
      role="dialog"
      aria-modal="true"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      {children}
    </div>,
    document.body
  );
}
